import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { CreditFooter } from "@/components/CreditFooter";
import { CreditCost } from "@/components/CreditCost";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  ArrowRight, ArrowLeft, Instagram, Music2, Facebook, Linkedin, Check, Target,
  Users, AlertCircle, Sparkles, Plus, Loader2, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import { FieldHint } from "@/components/FieldHint";

const PLATFORMS = [
  { id: "instagram", label: "Instagram", icon: Instagram },
  { id: "tiktok", label: "TikTok", icon: Music2 },
  { id: "facebook", label: "Facebook", icon: Facebook },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
];

const GOALS = [
  { id: "build_audience", label: "Build audience" },
  { id: "generate_leads", label: "Generate leads" },
  { id: "sell_products", label: "Sell digital products" },
  { id: "promote_services", label: "Promote services" },
  { id: "promote_affiliate", label: "Promote affiliate offers" },
  { id: "educate", label: "Educate audience" },
];

const OFFER_TYPES = ["course", "coaching", "template", "toolkit", "service", "workshop", "other"];

export default function NewCampaign() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshCredits } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // selected offer profile object
  const [creating, setCreating] = useState(false);
  const [adjust, setAdjust] = useState(false);
  const [angleSug, setAngleSug] = useState([]);
  const [angleLoading, setAngleLoading] = useState(false);
  const [usedAngles, setUsedAngles] = useState([]);
  const [override, setOverride] = useState({ audience: "", problem: "", offer_type: "course" });
  const [form, setForm] = useState({
    name: "",
    goal: "build_audience",
    angle: "",
    platforms: ["instagram", "tiktok"],
    num_lessons: 7,
    calendar_days: 7,
    source_material: "",
  });

  useEffect(() => {
    // Prefill from Income Mode "Save to Campaign" if present.
    const raw = sessionStorage.getItem("h2i_campaign_prefill");
    if (raw) {
      try {
        const p = JSON.parse(raw);
        setForm((f) => ({
          ...f,
          angle: p.angle || f.angle,
          source_material: p.source_material || f.source_material,
        }));
        toast.success("Hook & script added from Income Mode");
      } catch (e) { console.warn("Could not parse campaign prefill from sessionStorage:", e); }
      sessionStorage.removeItem("h2i_campaign_prefill");
    }
    api.get("/projects/active")
      .then((r) => {
        const proj = r.data.project;
        setProfiles(proj ? [proj] : []);
        if (proj?.offer) pickProfile({ ...proj.offer, projectName: proj.name });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setOv = (k, v) => setOverride((o) => ({ ...o, [k]: v }));
  const togglePlatform = (id) =>
    set("platforms", form.platforms.includes(id)
      ? form.platforms.filter((p) => p !== id)
      : [...form.platforms, id]);

  const pickProfile = (p) => {
    setSelected(p);
    setOverride({ audience: p.audience || "", problem: p.problem || "", offer_type: p.offer_type || "course" });
    setUsedAngles([]);
    api.get(`/offer-profiles/${p.id}/angles`)
      .then((r) => setUsedAngles(r.data.angles || []))
      .catch(() => {});
  };

  const suggestAngle = async () => {
    if (!selected) return;
    setAngleLoading(true);
    setAngleSug([]);
    const base = adjust ? override : selected;
    try {
      const { data } = await api.post("/offer-profiles/suggest", {
        field: "angle",
        offer_profile_id: selected.id,
        answers: {
          selling: selected.selling,
          audience: base.audience || selected.audience,
          problem: base.problem || selected.problem,
          result: selected.result,
          offer_type: base.offer_type || selected.offer_type,
          goal: (GOALS.find((g) => g.id === form.goal) || {}).label || form.goal,
        },
      });
      setAngleSug(data.suggestions || []);
      refreshCredits();
    } catch (e) {
      if (e.response?.status !== 402) toast.error("Couldn't get angle ideas right now. Try again.");
    } finally {
      setAngleLoading(false);
    }
  };

  const create = async () => {
    if (form.platforms.length === 0) {
      toast.info("Pick at least one platform");
      return;
    }
    setCreating(true);
    const payload = {
      ...form,
      offer_profile_id: selected.id,
      offer_override: adjust
        ? { audience: override.audience, problem: override.problem, offer_type: override.offer_type }
        : {},
    };
    try {
      const { data } = await api.post("/campaigns", payload);
      toast.success("Campaign created");
      navigate(`/campaigns/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create campaign");
      setCreating(false);
    }
  };

  if (loading) {
    return <p className="text-muted-foreground">Loading...</p>;
  }

  // No profiles yet — nudge to create one first.
  if (profiles.length === 0) {
    return (
      <div className="mx-auto max-w-xl" data-testid="new-campaign-no-profile">
        <Card className="flex flex-col items-center gap-4 border-dashed border-border p-12 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary">
            <Target size={26} className="text-primary" />
          </div>
          <div>
            <p className="font-head text-lg font-semibold">Create a project first</p>
            <p className="text-sm text-muted-foreground">
              Campaigns build from your active project's offer. Set one up and you're ready to go.
            </p>
          </div>
          <Button onClick={() => navigate("/projects/new")} data-testid="create-first-profile-btn"
            className="rounded-full bg-primary hover:bg-primary/90">
            <Plus size={18} className="mr-1" /> Create project
          </Button>
        </Card>
      </div>
    );
  }

  // Step 1 — choose which profile to build from.
  if (!selected) {
    return (
      <div className="mx-auto max-w-3xl" data-testid="new-campaign-select-profile">
        <div className="mb-8 fade-up">
          <h1 className="font-head text-4xl font-bold">New campaign</h1>
          <p className="mt-2 text-muted-foreground">Which offer is this campaign for?</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {profiles.map((p) => (
            <button key={p.id} onClick={() => pickProfile(p)} data-testid={`select-profile-${p.id}`}
              className="group rounded-2xl border border-border bg-card p-6 text-left transition-transform duration-200 hover:-translate-y-1 hover:border-primary/50 hover:shadow-lg">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-block rounded-md bg-secondary px-2 py-0.5 text-xs font-medium capitalize text-secondary-foreground">
                  {p.offer_type}
                </span>
                {!p.pitch_test_done && (
                  <span data-testid={`pitch-needed-${p.id}`}
                    className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                    <Megaphone size={11} /> Pitch Test needed
                  </span>
                )}
              </div>
              <h3 className="mt-2 font-head text-xl font-bold group-hover:text-primary">{p.name}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{p.selling}</p>
            </button>
          ))}
          <button onClick={() => navigate("/offer-profiles/new")} data-testid="add-profile-from-wizard"
            className="flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border p-6 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary">
            <Plus size={22} />
            <span className="text-sm font-medium">New offer profile</span>
          </button>
        </div>
      </div>
    );
  }

  // Gate — the course builder is locked until this profile's Pitch Test is complete.
  if (!selected.pitch_test_done) {
    return (
      <div className="mx-auto max-w-xl" data-testid="pitch-gate">
        <button onClick={() => setSelected(null)} data-testid="change-profile-btn"
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
          <ArrowLeft size={15} /> Choose a different offer
        </button>
        <Card className="flex flex-col items-center gap-4 border-dashed border-amber-300 bg-amber-50/50 p-12 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
            <Megaphone size={26} />
          </div>
          <div>
            <p className="font-head text-lg font-semibold">Pitch Test required</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Before building a course for <span className="font-semibold text-foreground">{selected.name}</span>,
              run a quick Pitch Test to make sure the offer converts.
            </p>
          </div>
          <Button onClick={() => navigate(`/pitch-test/${selected.id}`)} data-testid="go-pitch-test-btn"
            className="rounded-full bg-primary px-6 hover:bg-primary/90">
            <Megaphone size={16} className="mr-1.5" /> Run Pitch Test<CreditCost n={5} />
          </Button>
        </Card>
      </div>
    );
  }

  // Step 2 — per-campaign details.
  return (
    <div className="mx-auto max-w-3xl" data-testid="new-campaign-wizard">
      <div className="mb-6 fade-up">
        <button onClick={() => setSelected(null)} data-testid="change-profile-btn"
          className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
          <ArrowLeft size={15} /> Change offer profile
        </button>
        <h1 className="font-head text-4xl font-bold">New campaign</h1>
        <p className="mt-2 text-muted-foreground">Just the details unique to this campaign.</p>
      </div>

      {/* Selected profile summary */}
      <div className="mb-6 rounded-xl border border-primary/20 bg-primary/5 p-4" data-testid="selected-profile-summary">
        <div className="flex items-start gap-3">
          <Target size={18} className="mt-0.5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{selected.name}</p>
            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
              <p className="flex items-start gap-2"><Users size={14} className="mt-0.5 shrink-0" />
                <span><span className="font-medium text-foreground">Audience:</span> {adjust ? override.audience : selected.audience || "—"}</span></p>
              <p className="flex items-start gap-2"><AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span><span className="font-medium text-foreground">Problem:</span> {adjust ? override.problem : selected.problem || "—"}</span></p>
              <p className="flex items-start gap-2"><Sparkles size={14} className="mt-0.5 shrink-0" />
                <span className="capitalize"><span className="font-medium text-foreground">Offer type:</span> {adjust ? override.offer_type : selected.offer_type}</span></p>
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 border-t border-primary/10 pt-3">
          <Switch checked={adjust} onCheckedChange={setAdjust} data-testid="adjust-toggle" />
          <span className="text-sm text-muted-foreground">Adjust for this campaign only (won't change the saved profile)</span>
        </div>
        {adjust && (
          <div className="mt-4 space-y-4" data-testid="override-fields">
            <div className="space-y-2">
              <Label>Audience (this campaign)
                <FieldHint testid="hint-override-audience" text="Target just this campaign at a slightly different group without changing your saved profile." />
              </Label>
              <Textarea data-testid="override-audience" rows={2} value={override.audience}
                onChange={(e) => setOv("audience", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Problem (this campaign)
                <FieldHint testid="hint-override-problem" text="Highlight a different pain point for this campaign only. Your saved profile stays as is." />
              </Label>
              <Textarea data-testid="override-problem" rows={2} value={override.problem}
                onChange={(e) => setOv("problem", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Offer type (this campaign)
                <FieldHint testid="hint-override-type" text="Switch the offer format for this campaign only — handy when you package the same offer differently." />
              </Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {OFFER_TYPES.map((t) => (
                  <button key={t} type="button" onClick={() => setOv("offer_type", t)}
                    data-testid={`override-type-${t}`}
                    className={`rounded-lg border py-2 text-xs font-semibold capitalize transition-colors ${
                      override.offer_type === t ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/40"
                    }`}>{t}</button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <Card className="border-border p-6 space-y-8 sm:p-8">
        <div className="space-y-2">
          <Label>Campaign name
            <FieldHint testid="hint-campaign-name" text="A private label so you can find this campaign later. Leave it blank and we'll name it for you." />
          </Label>
          <Input data-testid="campaign-name" value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. September launch push" className="text-base" />
          <p className="text-xs text-muted-foreground">Leave blank and we'll name it for you.</p>
        </div>

        <div className="space-y-2">
          <Label>Campaign goal
            <FieldHint testid="hint-campaign-goal" text="What you want this campaign to achieve — grow followers, get leads, or make sales. It shapes every hook and call-to-action." />
          </Label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {GOALS.map((g) => (
              <button key={g.id} onClick={() => set("goal", g.id)} data-testid={`goal-${g.id}`}
                className={`rounded-lg border py-2.5 text-sm font-semibold transition-colors ${
                  form.goal === g.id ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border hover:border-primary/40"
                }`}>{g.label}</button>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-3 block">Target platforms
            <FieldHint testid="hint-campaign-platforms" text="Where you'll post. We size and format each piece of content for the platforms you pick." />
          </Label>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {PLATFORMS.map((p) => {
              const Icon = p.icon;
              const active = form.platforms.includes(p.id);
              return (
                <button key={p.id} onClick={() => togglePlatform(p.id)} data-testid={`platform-${p.id}`}
                  className={`relative flex flex-col items-center gap-2 rounded-xl border p-4 transition-transform duration-200 hover:-translate-y-0.5 ${
                    active ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border"
                  }`}>
                  {active && <Check size={14} className="absolute right-2 top-2 text-primary" />}
                  <Icon size={22} className={active ? "text-primary" : "text-muted-foreground"} />
                  <span className="text-sm font-medium">{p.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Course length
              <FieldHint testid="hint-campaign-lessons" text="How many bite-sized lessons your mini-course will have. More lessons means more depth, but more to create." />
            </Label>
            <div className="flex gap-2">
              {[7, 10, 16].map((n) => (
                <button key={n} onClick={() => set("num_lessons", n)} data-testid={`lessons-${n}`}
                  className={`flex-1 rounded-lg border py-2.5 text-sm font-semibold transition-colors ${
                    form.num_lessons === n ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/40"
                  }`}>{n} lessons</button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Content calendar
              <FieldHint testid="hint-campaign-days" text="How many days of ready-to-post content we'll plan out for you across your chosen platforms." />
            </Label>
            <div className="flex gap-2">
              {[7, 14, 30].map((n) => (
                <button key={n} onClick={() => set("calendar_days", n)} data-testid={`days-${n}`}
                  className={`flex-1 rounded-lg border py-2.5 text-sm font-semibold transition-colors ${
                    form.calendar_days === n ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/40"
                  }`}>{n} days</button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Campaign angle or focus (optional)
            <FieldHint testid="hint-campaign-angle" text="A specific spin or theme for this campaign so it feels fresh. Leave blank for a balanced mix, or tap 'Suggest ideas'." />
          </Label>
          <Textarea data-testid="campaign-angle" value={form.angle}
            onChange={(e) => set("angle", e.target.value)} rows={2}
            placeholder="e.g. Focus on the 'no time to cook' angle for new parents this month" />
          <div>
            <Button variant="outline" size="sm" onClick={suggestAngle} disabled={angleLoading}
              data-testid="angle-suggest-btn"
              className="rounded-full border-primary/40 text-primary hover:bg-primary/5">
              {angleLoading ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Sparkles size={15} className="mr-1.5" />}
              {angleLoading ? "Thinking..." : "Suggest ideas"}
            </Button>
            <span className="ml-2 text-xs text-muted-foreground">Costs 5 credits</span>
          </div>
          {angleSug.length > 0 && (
            <div className="space-y-2 pt-1" data-testid="angle-suggestions">
              {angleSug.map((s, i) => (
                <button key={`${s}-${i}`} type="button" onClick={() => { set("angle", s); setAngleSug([]); }}
                  data-testid={`angle-suggestion-${i}`}
                  className="block w-full rounded-xl border border-border bg-secondary/40 p-3 text-left text-sm transition-colors hover:border-primary/50 hover:bg-primary/5">
                  {s}
                </button>
              ))}
            </div>
          )}
          {usedAngles.length > 0 && (
            <div className="pt-2" data-testid="used-angles">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Already used for this offer
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {usedAngles.map((a, i) => (
                  <span key={`${a}-${i}`} data-testid={`used-angle-${i}`}
                    className="max-w-full truncate rounded-full bg-secondary px-3 py-1 text-xs text-muted-foreground">
                    {a}
                  </span>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">Suggestions steer clear of these.</p>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Source material (optional)
            <FieldHint testid="hint-campaign-source" text="Paste your own notes, an article, or a transcript and we'll shape the content around it. Optional." />
          </Label>
          <Textarea data-testid="campaign-source" value={form.source_material}
            onChange={(e) => set("source_material", e.target.value)} rows={4}
            placeholder="Paste an existing article, transcript, or notes to influence the output..." />
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="wizard-back">
            Cancel
          </Button>
          <Button onClick={create} disabled={creating} data-testid="wizard-create"
            className="rounded-full bg-primary px-6 hover:bg-primary/90">
            {creating ? "Creating..." : "Create campaign"} <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </Card>
      <CreditFooter className="mt-8" />
    </div>
  );
}
